import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OverlayModal from '../../components/ui/OverlayModal';
import { SystemInfoBox, SystemInformationSection } from '../../components/ui/SystemInformation';
import WizardSelect from '../../components/ui/WizardSelect';
import { lkStatusPillClass, type LkPublicationStatus } from '../../data/legalKnowledge';
import {
  addCaseLinkage,
  deleteCaseLinkage,
  fetchExtractedCaseOutcomeById,
  fetchExtractedCaseOutcomeByCaseRequest,
  fetchLinkableArchivedCases,
  getArchivedCaseDetailsPath,
  publishExtractedCaseOutcome,
  rejectExtractedCaseOutcome,
  resolveCaseLinkageTargetIds,
  retainExtractedCaseOutcomeDraft,
  updateCaseLinkage,
  type CaseLinkageRow,
  type ExtractedCaseOutcomeDetail,
} from '../../services/legalKnowledgeArchive';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { SectionCard } from '../wizard/WizardComponents';
import { CaseLinkageLink } from './CaseLinkageLink';
import type { LegalOutcomeNotificationNavigationState } from '../../utils/notificationNavigation';

const GRID_STYLE = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 } as const;

function FieldBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--t3)', fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{children}</div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function dash(value: string | null | undefined): string {
  if (!value || !value.trim()) return '—';
  return value;
}

export default function ViewExtractedCaseOutcomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const caseRequestIdParam = searchParams.get('caseRequestId');
  const statusParam = searchParams.get('status');
  const fromLegalOutcomeNotification = Boolean(
    (location.state as LegalOutcomeNotificationNavigationState | null)?.fromLegalOutcomeNotification,
  );

  const [detail, setDetail] = useState<ExtractedCaseOutcomeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [editedStatutes, setEditedStatutes] = useState('');
  const [editedSummary, setEditedSummary] = useState('');

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectReasonError, setRejectReasonError] = useState('');
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);

  const [linkageModal, setLinkageModal] = useState<
    { mode: 'add' } | { mode: 'edit'; linkage: CaseLinkageRow } | null
  >(null);
  const [linkageValue, setLinkageValue] = useState('');
  const [linkageOptions, setLinkageOptions] = useState<{ id: string; label: string }[]>([]);
  const [removeLinkageConfirm, setRemoveLinkageConfirm] = useState<CaseLinkageRow | null>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [linkageTargetIds, setLinkageTargetIds] = useState<Record<string, string>>({});
  const [linkageTargetsLoading, setLinkageTargetsLoading] = useState(false);
  const [primaryActionsReady, setPrimaryActionsReady] = useState(false);

  const lkStatusT = useCallback(
    (s: LkPublicationStatus) => t(`legalKnowledge.status.${s}`),
    [t],
  );

  const loadDetail = useCallback(async (resetEditable = true) => {
    setLoading(true);
    setError(null);
    const outcomeId = id?.trim();
    let redirected = false;
    try {
      let data: ExtractedCaseOutcomeDetail;
      if (caseRequestIdParam && statusParam) {
        data = await fetchExtractedCaseOutcomeByCaseRequest(caseRequestIdParam, statusParam);
      } else if (outcomeId) {
        data = await fetchExtractedCaseOutcomeById(outcomeId);
      } else {
        return;
      }
      setDetail(data);
      if (resetEditable) {
        setEditedStatutes(data.applicable_statutes ?? '');
        setEditedSummary(data.extracted_outcome_summary ?? '');
      }
    } catch (err: unknown) {
      if (fromLegalOutcomeNotification && outcomeId) {
        redirected = true;
        navigate(getArchivedCaseDetailsPath(outcomeId), { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : t('legalKnowledge.viewArchived.loadFailed'));
      setDetail(null);
    } finally {
      if (!redirected) setLoading(false);
    }
  }, [id, caseRequestIdParam, statusParam, fromLegalOutcomeNotification, navigate, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    const effectiveCaseRequestId = detail?.case_request_id || caseRequestIdParam;
    if (!linkageModal || !effectiveCaseRequestId) return;
    let active = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLinkageOptions([]);
    const linkageId = linkageModal.mode === 'edit' ? linkageModal.linkage.linkage_id : undefined;
    void fetchLinkableArchivedCases(effectiveCaseRequestId, linkageId)
      .then((cases) => {
        if (!active) return;
        const options = cases.map((item) => ({
          id: item.extracted_case_outcome_id,
          label: item.archive_reference_number,
        }));
        setLinkageOptions(options);
        if (linkageModal.mode === 'edit') {
          const currentId =
            linkageModal.linkage.linked_extracted_case_outcome_id ??
            options.find((option) => option.label === linkageModal.linkage.archive_reference_number)?.id ??
            '';
          setLinkageValue(currentId);
        }
      })
      .catch(() => {
        if (active) setLinkageOptions([]);
      });
    return () => {
      active = false;
    };
  }, [linkageModal, detail?.case_request_id, caseRequestIdParam]);

  useEffect(() => {
    if (!detail?.case_linkages.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLinkageTargetIds({});
      setLinkageTargetsLoading(false);
      return;
    }
    let active = true;
    setLinkageTargetsLoading(true);
    void resolveCaseLinkageTargetIds(detail.case_linkages)
      .then((targets) => {
        if (active) setLinkageTargetIds(targets);
      })
      .finally(() => {
        if (active) setLinkageTargetsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [detail?.case_linkages]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPrimaryActionsReady(false);
    const timer = window.setTimeout(() => setPrimaryActionsReady(true), 150);
    return () => clearTimeout(timer);
  }, [id, caseRequestIdParam, statusParam]);

  useEffect(() => {
    if (!detail || loading) return;
    const status = detail.status_code.toLowerCase();
    if (status !== 'draft') {
      navigate(`/legal-knowledge/archive/${detail.id}`, { replace: true });
    }
  }, [detail, loading, navigate]);

  if (loading) {
    return (
      <>
        <div className="bc">
          <span>{t('nav.groups.legalKnowledgeArchiveManagement')}</span>
          <span>/</span>
          <span>{t('nav.extractedCaseOutcomes')}</span>
        </div>
        <div style={{ padding: '24px 0', color: 'var(--t3)' }}>{t('common.loading')}</div>
      </>
    );
  }

  if (!detail || error) {
    return (
      <>
        <div className="bc">
          <span>{t('nav.groups.legalKnowledgeArchiveManagement')}</span>
          <span>/</span>
          <span>{t('nav.extractedCaseOutcomes')}</span>
        </div>
        <div style={{ padding: '24px 0', fontSize: 14, color: 'var(--text)' }}>
          {error ?? t('legalKnowledge.viewOutcome.recordNotFound')}{' '}
          <Link to="/legal-knowledge/archive/management/outcomes" style={{ color: 'var(--blue)' }}>
            {t('legalKnowledge.viewOutcome.backToList')}
          </Link>
        </div>
      </>
    );
  }

  const status = detail.status_code.toLowerCase() as LkPublicationStatus;
  const isDraft = status === 'draft';
  const showCourtFields = detail.file_category === 'Civil Division - Advise';
  const showLitigationFields = detail.file_category?.startsWith('Civil Division - Litigation');

  if (!isDraft) {
    return (
      <>
        <div className="bc">
          <span>{t('nav.groups.legalKnowledgeArchiveManagement')}</span>
          <span>/</span>
          <span>{t('nav.extractedCaseOutcomes')}</span>
        </div>
        <div style={{ padding: '24px 0', color: 'var(--t3)' }}>{t('common.loading')}</div>
      </>
    );
  }

  const displayStatutes = editMode ? editedStatutes : (detail.applicable_statutes ?? '');
  const displaySummary = editMode ? editedSummary : (detail.extracted_outcome_summary ?? '');

  const handleEdit = () => {
    setEditMode(true);
    setEditedStatutes(detail.applicable_statutes ?? '');
    setEditedSummary(detail.extracted_outcome_summary ?? '');
  };

  const handleCancelEdit = () => {
    if (editMode) {
      setEditMode(false);
      setEditedStatutes(detail.applicable_statutes ?? '');
      setEditedSummary(detail.extracted_outcome_summary ?? '');
      return;
    }
    setCancelConfirmOpen(true);
  };

  const validateEditableFields = (): boolean => {
    if (!editedStatutes.trim()) {
      setFlashToast({ message: t('legalKnowledge.viewOutcome.statutesRequired'), variant: 'error' });
      return false;
    }
    if (!editedSummary.trim()) {
      setFlashToast({ message: t('legalKnowledge.viewOutcome.summaryRequired'), variant: 'error' });
      return false;
    }
    return true;
  };

  const handleRetainAsDraft = async () => {
    if (!validateEditableFields()) return;
    setSaving(true);
    try {
      const updated = await retainExtractedCaseOutcomeDraft({
        case_request_id: detail.case_request_id || caseRequestIdParam || '',
        applicable_statutes: editedStatutes.trim(),
        extracted_outcome_summary: editedSummary.trim(),
      });
      setDetail(updated);
      setEditMode(false);
      setFlashToast({ message: t('legalKnowledge.viewOutcome.draftRetainedSuccess'), variant: 'success' }, { persist: true });
    } catch (err: unknown) {
      setFlashToast({
        message: err instanceof Error ? err.message : t('legalKnowledge.viewOutcome.saveFailed'),
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!validateEditableFields()) return;
    setSaving(true);
    try {
      await publishExtractedCaseOutcome({
        case_request_id: detail.case_request_id || caseRequestIdParam || '',
        applicable_statutes: editedStatutes.trim(),
        extracted_outcome_summary: editedSummary.trim(),
      });
      setFlashToast({ message: t('legalKnowledge.viewOutcome.publishSuccess'), variant: 'success' }, { persist: true });
      navigate('/legal-knowledge/archive/management/outcomes');
    } catch (err: unknown) {
      setFlashToast({
        message: err instanceof Error ? err.message : t('legalKnowledge.viewOutcome.publishFailed'),
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRejectConfirm = async () => {
    setSaving(true);
    try {
      await rejectExtractedCaseOutcome({
        case_request_id: detail.case_request_id || caseRequestIdParam || '',
        reject_reason: rejectReason.trim(),
      });
      setRejectConfirmOpen(false);
      setRejectOpen(false);
      setFlashToast({ message: t('legalKnowledge.viewOutcome.rejectSuccess'), variant: 'success' }, { persist: true });
      navigate('/legal-knowledge/archive/management/outcomes');
    } catch (err: unknown) {
      setFlashToast({
        message: err instanceof Error ? err.message : t('legalKnowledge.viewOutcome.rejectFailed'),
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLinkage = async () => {
    if (!linkageValue || !id) return;
    setSaving(true);
    try {
      if (linkageModal?.mode === 'add') {
        await addCaseLinkage(id, { linked_extracted_case_outcome_id: linkageValue });
      } else if (linkageModal?.mode === 'edit') {
        await updateCaseLinkage(id, linkageModal.linkage.linkage_id, {
          linked_extracted_case_outcome_id: linkageValue,
        });
      }
      setLinkageModal(null);
      await loadDetail(false);
      setFlashToast({ message: t('legalKnowledge.viewOutcome.linkageSavedSuccess'), variant: 'success' });
    } catch (err: unknown) {
      setFlashToast({
        message: err instanceof Error ? err.message : t('legalKnowledge.viewOutcome.linkageSaveFailed'),
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLinkage = async () => {
    if (!removeLinkageConfirm || !id) return;
    setSaving(true);
    try {
      await deleteCaseLinkage(id, removeLinkageConfirm.linkage_id);
      setRemoveLinkageConfirm(null);
      await loadDetail(false);
      setFlashToast({ message: t('legalKnowledge.viewOutcome.linkageRemovedSuccess'), variant: 'success' });
    } catch (err: unknown) {
      setFlashToast({
        message: err instanceof Error ? err.message : t('legalKnowledge.viewOutcome.linkageRemoveFailed'),
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.legalKnowledgeArchiveManagement')}</span>
        <span>/</span>
        <span>{t('nav.extractedCaseOutcomes')}</span>
        <span>/</span>
        <span>{t('legalKnowledge.viewOutcome.draftBreadcrumb')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('legalKnowledge.viewOutcome.draftTitle')}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button type="button" className="mfb" disabled={saving || !primaryActionsReady} onClick={handleCancelEdit}>
            {t('common.cancel')}
          </button>
          {!editMode ? (
            <>
              <button
                type="button"
                className="mfb"
                style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                disabled={saving || !primaryActionsReady}
                onClick={() => {
                  setRejectOpen(true);
                  setRejectReason('');
                  setRejectReasonError('');
                }}
              >
                {t('legalKnowledge.archive.reject')}
              </button>
              <button type="button" className="mfb" disabled={saving || !primaryActionsReady} onClick={handleEdit}>
                {t('legalKnowledge.viewOutcome.edit')}
              </button>
              <button
                type="button"
                className="mfb mfb-dk"
                disabled={saving || !primaryActionsReady}
                onClick={() => void handlePublish()}
              >
                {t('legalKnowledge.viewOutcome.approveAndPublish')}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="mfb mfb-dk"
              disabled={saving || !primaryActionsReady}
              onClick={() => void handleRetainAsDraft()}
            >
              {t('legalKnowledge.viewOutcome.retainAsDraft')}
            </button>
          )}
        </div>
      </div>

      <SectionCard title={t('legalKnowledge.viewOutcome.caseOverview')}>
        <div style={GRID_STYLE}>
          <FieldBlock label={t('legalKnowledge.viewOutcome.archiveReferenceNumber')}>
            {detail.archive_reference_number}
          </FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.fileReferenceNumber')}>
            {detail.file_reference_number}
          </FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.caseCompletedAt')}>
            {detail.case_completed_at ? formatDisplayDateTime(detail.case_completed_at) : '—'}
          </FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.outcomeExtractedAt')}>
            {detail.outcome_extracted_at ? formatDisplayDateTime(detail.outcome_extracted_at) : '—'}
          </FieldBlock>

          <FieldBlock label={t('common.status')}>
            <span className={lkStatusPillClass(status)}>{lkStatusT(status)}</span>
          </FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.fileCategory')}>{detail.file_category}</FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.subFileCategory')}>
            {dash(detail.sub_file_category)}
          </FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.applicableAreas')}>
            {dash(detail.applicable_areas)}
          </FieldBlock>

          <FieldBlock label={t('legalKnowledge.viewOutcome.requestSubmittedAt')}>
            {detail.request_submitted_at ? formatDisplayDateTime(detail.request_submitted_at) : '—'}
          </FieldBlock>
          <FieldBlock
            label={
              detail.division_name != null
                ? t('legalKnowledge.viewOutcome.divisionName')
                : t('legalKnowledge.viewOutcome.submittedAgency')
            }
          >
            {dash(detail.division_name ?? detail.submitted_agency)}
          </FieldBlock>
          <div style={{ gridColumn: 'span 2' }}>
            <FieldBlock label={t('legalKnowledge.viewOutcome.description')}>
              <span style={{ whiteSpace: 'pre-wrap' }}>{dash(detail.description)}</span>
            </FieldBlock>
          </div>

          <FieldBlock label={t('legalKnowledge.viewOutcome.province')}>{dash(detail.province)}</FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.district')}>{dash(detail.district)}</FieldBlock>
          <div />
          <div />

          <FieldBlock label={t('legalKnowledge.viewOutcome.caseNumber')}>{dash(detail.case_number)}</FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.otherExternalRefNos')}>
            {dash(detail.other_external_reference_numbers)}
          </FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.connectedAgdFileRefNo')}>
            {dash(detail.connected_agd_file_reference_number)}
          </FieldBlock>
          {showCourtFields ? (
            <>
              <FieldBlock label={t('legalKnowledge.viewOutcome.courtType')}>{dash(detail.court_type)}</FieldBlock>
              <FieldBlock label={t('legalKnowledge.viewOutcome.courtName')}>{dash(detail.court_name)}</FieldBlock>
            </>
          ) : (
            <div />
          )}

          {showLitigationFields ? (
            <>
              <div style={{ gridColumn: 'span 2' }}>
                <FieldBlock label={t('legalKnowledge.viewOutcome.subjectOfDispute')}>
                  <span style={{ whiteSpace: 'pre-wrap' }}>{dash(detail.subject_of_the_dispute)}</span>
                </FieldBlock>
              </div>
              <FieldBlock label={t('legalKnowledge.viewOutcome.incidentOccurredDate')}>
                {detail.incident_occurred_date ? formatDisplayDate(detail.incident_occurred_date) : '—'}
              </FieldBlock>
              <div />
            </>
          ) : null}

          <div style={{ gridColumn: '1 / -1' }}>
            <FieldBlock
              label={
                editMode
                  ? `${t('legalKnowledge.viewOutcome.applicableStatutes')} *`
                  : t('legalKnowledge.viewOutcome.applicableStatutes')
              }
            >
              {editMode ? (
                <textarea
                  className="ccr-inp"
                  style={{ width: '100%', minHeight: 80, resize: 'vertical', boxSizing: 'border-box' }}
                  value={editedStatutes}
                  maxLength={255}
                  placeholder={t('legalKnowledge.viewOutcome.statutesPlaceholder')}
                  onChange={(e) => setEditedStatutes(e.target.value)}
                />
              ) : (
                <span style={{ whiteSpace: 'pre-wrap' }}>{displayStatutes || '-'}</span>
              )}
            </FieldBlock>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <FieldBlock
              label={
                editMode
                  ? `${t('legalKnowledge.viewOutcome.extractedOutcomeSummary')} *`
                  : t('legalKnowledge.viewOutcome.extractedOutcomeSummary')
              }
            >
              {editMode ? (
                <textarea
                  className="ccr-inp"
                  style={{ width: '100%', minHeight: 120, resize: 'vertical', boxSizing: 'border-box' }}
                  value={editedSummary}
                  maxLength={2000}
                  placeholder={t('legalKnowledge.viewOutcome.summaryPlaceholder')}
                  onChange={(e) => setEditedSummary(e.target.value)}
                />
              ) : (
                <span style={{ whiteSpace: 'pre-wrap' }}>{displaySummary || '-'}</span>
              )}
            </FieldBlock>
          </div>
        </div>
      </SectionCard>

      <SectionCard title={t('legalKnowledge.viewOutcome.caseLinkages')}>
        {editMode ? (
          <button
            type="button"
            className="mfb"
            style={{ marginBottom: 12 }}
            disabled={saving}
            onClick={() => {
              setLinkageModal({ mode: 'add' });
              setLinkageValue('');
            }}
          >
            {t('legalKnowledge.viewOutcome.addLinkage')}
          </button>
        ) : null}

        {detail.case_linkages.length === 0 ? (
          <div style={{ color: 'var(--t3)', fontSize: 13 }}>{t('legalKnowledge.viewOutcome.noCaseLinkages')}</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {detail.case_linkages.map((link) => (
              <div
                key={link.linkage_id}
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}
              >
                {editMode ? (
                  <span
                    className="mfb"
                    style={{ textDecoration: 'none', color: 'var(--text)', cursor: 'default', pointerEvents: 'none' }}
                  >
                    {link.archive_reference_number}
                  </span>
                ) : (
                  <CaseLinkageLink
                    archiveReferenceNumber={link.archive_reference_number}
                    targetId={linkageTargetIds[link.linkage_id]}
                    resolving={linkageTargetsLoading && !linkageTargetIds[link.linkage_id]}
                  />
                )}
                {editMode ? (
                  <>
                    <button
                      className="act-b"
                      type="button"
                      title={t('legalKnowledge.viewOutcome.editLinkageTip')}
                      disabled={saving}
                      onClick={() => {
                        setLinkageModal({ mode: 'edit', linkage: link });
                        setLinkageValue(link.linked_extracted_case_outcome_id ?? '');
                      }}
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="act-b"
                      type="button"
                      title={t('legalKnowledge.viewOutcome.removeLinkageTip')}
                      disabled={saving}
                      onClick={() => setRemoveLinkageConfirm(link)}
                    >
                      <TrashIcon />
                    </button>
                  </>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <SystemInformationSection>
        <SystemInfoBox
          label={t('legalKnowledge.viewOutcome.lastUpdatedAt')}
          value={detail.last_updated_at ? formatDisplayDateTime(detail.last_updated_at) : '—'}
        />
        <SystemInfoBox label={t('legalKnowledge.viewOutcome.lastUpdatedBy')} value={dash(detail.last_updated_by)} />
      </SystemInformationSection>

      <OverlayModal
        isOpen={rejectOpen}
        overlayClassName="overlay"
        contentClassName="cn-mbox"
        centerVertically
        onClose={() => setRejectOpen(false)}
      >
        <div className="cn-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{t('legalKnowledge.viewOutcome.rejectModalTitle')}</span>
          <button className="cn-x" type="button" aria-label={t('common.close')} onClick={() => setRejectOpen(false)}>
            <CloseIcon />
          </button>
        </div>
        <div className="cn-body">
          <div className="ccr-fg">
            <label>
              {t('legalKnowledge.viewOutcome.rejectReason')} <span className="req">*</span>
            </label>
            <textarea
              className="ccr-inp"
              style={{ width: '100%', minHeight: 160, resize: 'vertical', boxSizing: 'border-box' }}
              value={rejectReason}
              maxLength={2000}
              placeholder={t('legalKnowledge.viewOutcome.rejectReasonPlaceholder')}
              onChange={(e) => {
                setRejectReason(e.target.value);
                if (e.target.value.trim()) setRejectReasonError('');
              }}
            />
            {rejectReasonError ? (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>{rejectReasonError}</div>
            ) : null}
          </div>
        </div>
        <div className="cn-footer">
          <button className="mfb" type="button" onClick={() => setRejectOpen(false)}>
            {t('common.cancel')}
          </button>
          <button
            className="mfb mfb-dk"
            type="button"
            onClick={() => {
              if (!rejectReason.trim()) {
                setRejectReasonError(t('legalKnowledge.viewOutcome.rejectReasonRequired'));
                return;
              }
              setRejectConfirmOpen(true);
            }}
          >
            {t('legalKnowledge.archive.reject')}
          </button>
        </div>
      </OverlayModal>

      <OverlayModal
        isOpen={linkageModal !== null}
        overlayClassName="overlay"
        contentClassName="cn-mbox"
        centerVertically
        onClose={() => setLinkageModal(null)}
      >
        <div className="cn-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            {linkageModal?.mode === 'edit'
              ? t('legalKnowledge.viewOutcome.editCaseLinkage')
              : t('legalKnowledge.viewOutcome.addNewCaseLinkage')}
          </span>
          <button className="cn-x" type="button" aria-label={t('common.close')} onClick={() => setLinkageModal(null)}>
            <CloseIcon />
          </button>
        </div>
        <div className="cn-body">
          <div className="ccr-fg">
            <label>
              {t('legalKnowledge.viewOutcome.linkToArchivedCase')} <span className="req">*</span>
            </label>
            <WizardSelect
              id="lka-linkage-select"
              value={linkageValue}
              placeholder={t('legalKnowledge.viewOutcome.linkToArchivedCasePlaceholder')}
              options={linkageOptions}
              onChange={setLinkageValue}
            />
          </div>
        </div>
        <div className="cn-footer">
          <button className="mfb" type="button" onClick={() => setLinkageModal(null)}>
            {t('common.cancel')}
          </button>
          <button
            className="mfb mfb-dk"
            type="button"
            disabled={!linkageValue || saving}
            onClick={() => void handleSaveLinkage()}
          >
            {t('common.save')}
          </button>
        </div>
      </OverlayModal>

      <ConfirmDialog
        isOpen={rejectConfirmOpen}
        title={t('legalKnowledge.viewOutcome.rejectConfirmTitle')}
        message={t('legalKnowledge.viewOutcome.rejectConfirmMessage')}
        onClose={() => setRejectConfirmOpen(false)}
        onConfirm={() => void handleRejectConfirm()}
      />

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('legalKnowledge.viewOutcome.cancelConfirmTitle')}
        message={t('legalKnowledge.viewOutcome.cancelConfirmMessage')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => navigate('/legal-knowledge/archive/management/outcomes')}
      />

      <ConfirmDialog
        isOpen={removeLinkageConfirm !== null}
        title={t('legalKnowledge.viewOutcome.removeLinkageConfirmTitle')}
        message={t('legalKnowledge.viewOutcome.removeLinkageConfirmMessage')}
        onClose={() => setRemoveLinkageConfirm(null)}
        onConfirm={() => void handleRemoveLinkage()}
      />
    </>
  );
}
